import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './contexts/ThemeContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { ChatProvider, useChat } from './contexts/ChatContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ChatBubble from './components/ChatBubble';
import ChatModal from './components/ChatModal';
import DebugQuestGame from './components/DebugQuestGame';
import { Terminal } from './components/Terminal';
import CookieConsent from './components/CookieConsent';
import HomePage from './pages/HomePage';
import WorkPage from './pages/WorkPage';
import WorkProjectPage from './pages/WorkProjectPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import { trackPageView } from './utils/analytics';

const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminChatsPage = lazy(() => import('./pages/admin/AdminChatsPage'));
const AdminContactsPage = lazy(() => import('./pages/admin/AdminContactsPage'));
const AdminVisitorsPage = lazy(() => import('./pages/admin/AdminVisitorsPage'));
const AdminArticlesPage = lazy(() => import('./pages/admin/AdminArticlesPage'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));

function AdminFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Opt out of the browser's automatic scroll restoration on back/forward —
    // our explicit scroll-to-top / scroll-to-anchor logic below should always
    // win, otherwise routes with async data can land mid-page.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (hash) {
      // Target element may not exist yet if the new route is still loading
      // async data; retry across a few frames before giving up.
      const targetId = hash.slice(1);
      const headerOffset = 100;
      const deadline = performance.now() + 800;

      const tryScroll = () => {
        const el = document.getElementById(targetId);
        if (el) {
          const currentScroll =
            document.documentElement.scrollTop || document.body.scrollTop || window.scrollY;
          const top = el.getBoundingClientRect().top + currentScroll - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
          document.documentElement.scrollTop = top;
          document.body.scrollTop = top;
          return;
        }
        if (performance.now() < deadline) {
          requestAnimationFrame(tryScroll);
        }
      };
      requestAnimationFrame(tryScroll);
    } else {
      // Scroll every candidate scroll root. body-as-scroll-container is a
      // historical quirk when body has overflow-x set; hitting all three
      // is cheap and bulletproof across browsers.
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [pathname, hash]);

  useEffect(() => {
    if (!pathname.startsWith('/admin')) {
      trackPageView(window.location.href);
    }
  }, [pathname]);

  return null;
}

function AppShell() {
  const { isOpen: isChatOpen, openChat, closeChat, consumeInitialMessage } = useChat();
  const [isGameOpen, setIsGameOpen] = useState(false);
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);
  const [konamiFlash, setKonamiFlash] = useState(false);

  useEffect(() => {
    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];
    let konamiIndex = 0;
    let flashTimer: number | undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          setKonamiFlash(true);
          if (flashTimer) window.clearTimeout(flashTimer);
          flashTimer = window.setTimeout(() => setKonamiFlash(false), 320);
          setIsGameOpen(true);
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (flashTimer) window.clearTimeout(flashTimer);
    };
  }, []);

  const openTerminal = () => setIsTerminalOpen(true);

  return (
    <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/admin/login" element={<Suspense fallback={<AdminFallback />}><AdminLoginPage /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<AdminFallback />}><AdminLayout /></Suspense>}>
              <Route index element={<Suspense fallback={<AdminFallback />}><AdminOverviewPage /></Suspense>} />
              <Route path="chats" element={<Suspense fallback={<AdminFallback />}><AdminChatsPage /></Suspense>} />
              <Route path="contacts" element={<Suspense fallback={<AdminFallback />}><AdminContactsPage /></Suspense>} />
              <Route path="visitors" element={<Suspense fallback={<AdminFallback />}><AdminVisitorsPage /></Suspense>} />
              <Route path="articles" element={<Suspense fallback={<AdminFallback />}><AdminArticlesPage /></Suspense>} />
            </Route>

            <Route
              path="*"
              element={
                <div className="min-h-screen bg-background text-text-primary flex flex-col">
                  <Header onOpenChat={() => openChat()} onOpenTerminal={openTerminal} />
                  <main className="flex-grow">
                    <Routes>
                      <Route path="/" element={<HomePage onOpenChat={() => openChat()} />} />
                      <Route path="/work" element={<WorkPage />} />
                      <Route path="/work/:slug" element={<WorkProjectPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/articles" element={<Suspense fallback={<AdminFallback />}><ArticlesPage /></Suspense>} />
                      <Route path="/articles/:slug" element={<Suspense fallback={<AdminFallback />}><ArticlePage /></Suspense>} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </main>
                  <Footer />
                  <ChatBubble onClick={() => openChat()} />
                  <ChatModal
                    isOpen={isChatOpen}
                    onClose={closeChat}
                    onOpenTerminal={openTerminal}
                    consumeInitialMessage={consumeInitialMessage}
                  />
                  <DebugQuestGame isOpen={isGameOpen} onClose={() => setIsGameOpen(false)} />
                  <AnimatePresence>
                    {isTerminalOpen && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm"
                        onClick={() => setIsTerminalOpen(false)}
                      >
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="w-full max-w-3xl"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Terminal
                            isModal={true}
                            onClose={() => setIsTerminalOpen(false)}
                            onExit={() => setIsTerminalOpen(false)}
                          />
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>
                    {konamiFlash && (
                      <motion.div
                        key="konami-flash"
                        aria-hidden
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="pointer-events-none fixed inset-0 z-[60] bg-accent-alt-ink mix-blend-screen"
                      />
                    )}
                  </AnimatePresence>
                  <CookieConsent />
                </div>
              }
            />
          </Routes>
        </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AdminAuthProvider>
        <ChatProvider>
          <AppShell />
        </ChatProvider>
      </AdminAuthProvider>
    </ThemeProvider>
  );
}

export default App;